import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import IntegrationStatusTile from './IntegrationStatusTile';
import { formatWhen } from '../../utils/crmSync';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

// The BOT sync tile on the booking overview (MS-6748), next to the CRM sync tile and in the same
// style. It shows the ERP RPA Bot's status for this booking (the BOT service's own statuses: new,
// pending, success, failed) and opens a small modal with the robot's message, the facts and the
// same actions the legacy "BOT Sync Status" box offers — Re-sync and Error logs — which stay
// gated exactly as before (the route ACL decides who may re-sync; the overview passes canResync).
//
// No ERP product name appears here: the caption says "ERP RPA Bot" for every tenant.
const ns = { ns: TRANSLATION_FILES.translation };

// The BOT service's status ids → the tile's words and tone. `failed` reads "Needs attention" like
// the CRM tile; `new` is a row the robot has not picked up.
export function botTile(status) {
  switch (String(status || '').toLowerCase()) {
    case 'pending': return { label: 'Pending', tone: 'waiting' };
    case 'success': return { label: 'Success', tone: 'done' };
    case 'failed': return { label: 'Needs attention', tone: 'attention' };
    case 'new': return { label: 'Not sent', tone: 'closed' };
    default: return { label: 'Not sent', tone: 'closed' };
  }
}

// The robot's newest error, as lines: entries carry a leading "[timestamp]" stamp, so the newest is
// the one with the greatest stamp (the array's order is not reliable -- the overview's Error Logs
// modal reverses it in place), falling back to the last entry when no stamp parses. The stamp is
// dropped and "<br>" becomes a line break; nothing of the message is discarded.
export function botMessageLines(errors) {
  const list = Array.isArray(errors) ? errors.filter(e => typeof e === 'string' && e.trim()) : [];
  if (!list.length) return [];
  const stampOf = (e) => {
    const m = /^\[([^\]]*)\]/.exec(e.trim());
    const ms = m ? Date.parse(m[1]) : NaN;
    return Number.isNaN(ms) ? null : ms;
  };
  let newest = list[list.length - 1];
  let best = null;
  list.forEach((e) => {
    const ms = stampOf(e);
    if (ms !== null && (best === null || ms >= best)) { best = ms; newest = e; }
  });
  return newest.trim().replace(/^\[[^\]]*\]\s*/, '')
    .split(/<br\s*\/?>/i)
    .map(l => l.trim())
    .filter(Boolean);
}

// botMessage is the first line of that newest error; '' when none.
export function botMessage(errors) {
  const lines = botMessageLines(errors);
  return lines.length ? lines[0] : '';
}

export class BotSyncPanel extends Component {
  static propTypes = {
    status: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
    serviceID: PropTypes.string,
    updatedAt: PropTypes.string,
    canResync: PropTypes.bool,
    syncing: PropTypes.bool,
    onResync: PropTypes.func,
    onShowErrors: PropTypes.func,
    t: PropTypes.func.isRequired,
  }

  static defaultProps = {
    status: '',
    errors: [],
    serviceID: '',
    updatedAt: '',
    canResync: false,
    syncing: false,
    onResync: null,
    onShowErrors: null,
  }

  state = { open: false };

  handleOpen = () => this.setState({ open: true });

  handleClose = () => this.setState({ open: false });

  handleResync = () => {
    const { onResync } = this.props;
    this.setState({ open: false });
    if (onResync) onResync();
  }

  handleShowErrors = () => {
    const { onShowErrors } = this.props;
    this.setState({ open: false });
    if (onShowErrors) onShowErrors();
  }

  render() {
    const {
      t, status, errors, serviceID, updatedAt, canResync, syncing, onResync, onShowErrors,
    } = this.props;
    const tile = botTile(status);
    const lines = botMessageLines(errors);
    const resyncable = canResync && onResync && status !== '' && status !== 'success';
    const when = formatWhen(updatedAt);
    return (
      <span>
        <IntegrationStatusTile
          name={t('BOT sync', ns)}
          state={syncing ? t('Syncing', ns) : t(tile.label, ns)}
          tone={syncing ? 'waiting' : tile.tone}
          icon="redo"
          loading={syncing}
          onClick={this.handleOpen}
          expanded={this.state.open}
          testId="bot-sync-open"
        />
        <Modal open={this.state.open} size="tiny" closeIcon onClose={this.handleClose}>
          <Modal.Header>{t('BOT sync', ns)} · {t(tile.label, ns)}</Modal.Header>
          <Modal.Content>
            <div className="bot-sync-detail" data-testid="bot-sync-detail">
              <div className="bot-sync-detail__meta">{t('ERP RPA Bot', ns)}</div>
              {tile.tone === 'attention' ? (
                <div>
                  <b>{t('The robot could not key this booking in.', ns)}</b>
                  {lines.length ? (
                    <div className="bot-sync-detail__message" data-testid="bot-sync-message">
                      {lines.map((line, i) => <div key={`${i}-${line}`}>{line}</div>)}
                    </div>
                  ) : null}
                  <div className="bot-sync-detail__meta">
                    {t('Fix what the message names (purchaser details, unit status), then Re-sync.', ns)}
                  </div>
                </div>
              ) : null}
              {tile.tone === 'waiting' ? <div>{t('The robot has this booking and will answer shortly.', ns)}</div> : null}
              {tile.tone === 'done' ? <div>{t('Keyed in by the robot.', ns)}</div> : null}
              {tile.tone === 'closed' ? <div>{t('Not picked up by the robot yet.', ns)}</div> : null}
              {serviceID ? <div className="bot-sync-detail__meta">{t('ID', ns)} {serviceID}</div> : null}
              {when ? <div className="bot-sync-detail__meta">{t('Updated', ns)} {when}</div> : null}
              <div className="bot-sync-detail__actions">
                {resyncable ? (
                  <Button size="small" compact primary disabled={syncing} loading={syncing} onClick={this.handleResync} data-testid="bot-sync-resync">
                    {t('Re-sync', ns)}
                  </Button>
                ) : null}
                {onShowErrors && errors && errors.length > 0 ? (
                  <Button size="small" compact basic onClick={this.handleShowErrors} data-testid="bot-sync-errors">
                    {t('Error logs', ns)}
                  </Button>
                ) : null}
              </div>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleClose}>{t('Close', ns)}</Button>
          </Modal.Actions>
        </Modal>
      </span>
    );
  }
}

export default withTranslation(namespaces)(BotSyncPanel);



// WEBPACK FOOTER //
// ./src/components/Crm/BotSyncPanel.jsx